<template>
  <v-container fluid>
    <v-row align="center" justify="center" class="mx-0">
      <v-col cols="12">
        <v-card outlined>
          <v-card-title>
            <v-row no-gutters>
              <v-col cols="12" md="6">
                <div class="text-h5">
                  Notifications ({{ businessPortalStats.noOfNewNotifications }})
                </div>
              </v-col>
              <v-col cols="12" md="6">
                <div class="text-right" v-if="selected.length > 0">
                  <v-tooltip bottom>
                    <template v-slot:activator="{ on, attrs }">
                      <v-btn
                        v-bind="attrs"
                        v-on="on"
                        small
                        text
                        fab
                        class="mr-5"
                        @click="deleteNotifications"
                      >
                        <v-icon class="primary--text">delete</v-icon>
                      </v-btn>
                    </template>
                    <span>Delete</span>
                  </v-tooltip>
                  <v-tooltip bottom>
                    <template v-slot:activator="{ on, attrs }">
                      <v-btn
                        v-bind="attrs"
                        v-on="on"
                        small
                        text
                        fab
                        class="mr-5"
                        @click="markNotificationAsUnread"
                      >
                        <v-icon class="primary--text">markunread</v-icon>
                      </v-btn>
                    </template>
                    <span>Mark as unread</span>
                  </v-tooltip>
                  <v-tooltip bottom>
                    <template v-slot:activator="{ on, attrs }">
                      <v-btn
                        v-bind="attrs"
                        v-on="on"
                        small
                        text
                        fab
                        @click="markNotificationAsRead"
                      >
                        <v-icon class="primary--text">drafts</v-icon>
                      </v-btn>
                    </template>
                    <span>Mark as read</span>
                  </v-tooltip>
                </div>
              </v-col>
            </v-row>
          </v-card-title>
          <v-card-text v-if="notifications.length > 0">
            <v-data-table
              class="row-pointer"
              v-model="selected"
              :headers="headers"
              :items="notifications"
              :items-per-page="20"
              :single-select="false"
              show-select
              item-key="notificationId"
              :disable-pagination="true"
              hide-default-footer
              disable-sort
              @click:row="handleNotificationClick"
            >
              <template v-slot:item.subject="{ item }">
                <v-list color="transparent">
                  <v-list-item>
                    <UserAvatar
                      :usersInitials="getInitials(item.userName)"
                      :uid="item.uid"
                      :businessCardId="item.businessCardId"
                    />
                    <v-list-item-content>
                      <v-list-item-title
                        v-bind:class="{ 'font-weight-bold': !item.isRead }"
                        >{{ item.subject }}</v-list-item-title
                      >
                    </v-list-item-content>
                  </v-list-item>
                </v-list>
              </template>
              <template v-slot:item.createdOn="{ item }">
                <timeago
                  v-bind:class="{ 'font-weight-bold': !item.isRead }"
                  style="font-size: 16px"
                  :datetime="Date.parse(item.createdOn)"
                  :auto-update="60"
                ></timeago>
              </template>
            </v-data-table>
            <v-row align="center" justify="center" class="mx-0 mt-5">
              <v-col>
                <p class="pa-0 ma-0 text-center">
                  <v-btn
                    v-if="
                      firstDocumentOnLoad != null &&
                      !isEndReached &&
                      notifications.length >= pageSize
                    "
                    outlined
                    depressed
                    color="secondary"
                    @click="handleLoadMore"
                    large
                    >Load More</v-btn
                  >
                </p>
              </v-col>
            </v-row>
            <HandleNotification
              :present="presentHandleNotificationComponent"
              :selectedNotification="selectedNotification"
              @handled="presentHandleNotificationComponent = false"
            />
            <!-- Start: Snackbar -->
            <v-snackbar
              v-model="snackbar"
              left
              multi-line
              :timeout="snackbarTimeout"
            >
              {{ snackbarText }}

              <template v-slot:action="{ attrs }">
                <v-btn
                  color="primary"
                  text
                  v-bind="attrs"
                  @click="snackbar = false"
                  top
                >
                  Close
                </v-btn>
              </template>
            </v-snackbar>
            <!-- End: Snackbar-->
          </v-card-text>
          <v-card-text v-else>
            <v-sheet height="300" tile>
              <v-row class="fill-height" align="center" justify="center">
                <v-card elevation="0">
                  <v-card-text>
                    <div class="text-center">
                      <v-icon color="grey lighten-2" size="85">mdi-bell</v-icon>
                    </div>
                    <div
                      class="
                        mt-2
                        text-center text--primary text-h6
                        font-weight-regular
                      "
                    >
                      No notifications yet
                    </div>
                    <div class="text-center">
                      Stay tuned! Notifications about your activity will show up
                      here.
                    </div>
                  </v-card-text>
                </v-card>
              </v-row>
            </v-sheet>
          </v-card-text>
        </v-card>
      </v-col>
    </v-row>
  </v-container>
</template>
<script>
// @ is an alias to /src
import { mapGetters, mapActions } from "vuex";

import UserAvatar from "@/components/UserAvatar";
import NotificationUtility from "@/shared/notification-utility";
import Utility from "@/shared/utility";
import HandleNotification from "@/components/HandleNotification";
import {
  businessPortalNotificationsDocRef,
  businessPortalStatsDocRef,
  getDownloadUrlForRef, getQueryBusinessPortalNotificationsCollection,
  getStorageRef, queryBusinessPortalNotificationsCollection
} from "@/shared/firebase_utils";
import {
  endBefore,
  increment,
  getFirestore,
  onSnapshot,
  orderBy,
  startAfter,
  startAt,
  where,
  writeBatch
} from "firebase/firestore";

export default {
  name: "Notifications",
  components: {
    UserAvatar,
    HandleNotification,
  },
  async created() {
    try {
      this.resetIsNewNotifications();
      this.initializeFirstDocumentOnLoad();
      if (this.firstDocumentOnLoad != null) {
        this.loadFirstPage();
      }
      this.loadNewDocuments();
    } catch (error) {
      console.log(error);
    }
  },
  methods: {
    ...mapActions(["resetIsNewNotifications", "setCurrentEvent"]),
    handleNotificationClick(item) {
      this.selectedNotification = item;

      //Start: Redirect to view registrants page for multi connect
      if (item.type == "MULTICONNECT_ATTENDEE_JOINED") {
        let event = this.multiConnectEvents.find(
          (event) => event.id == item.multiConnectEventId
        );

        this.setCurrentEvent(event);

        this.$router.push({
          name: "editMultiConnect",
          params: { eventId: event.id },
          query: { tab: 2 },
        });
      }
      //End: Redirect to view registrants page for multi connect

      this.presentHandleNotificationComponent = true;
    },
    handleLoadMore() {

        onSnapshot(
            queryBusinessPortalNotificationsCollection(
                where("businessProfileId", "==", this.userProfile.businessProfileId),
                orderBy("createdOn", "desc"),
                limit(this.pageSize),
                startAfter(this.lastLoadedDocumnet)
            ),
            (querySnapshot) => {
          if (querySnapshot.docs.length == 0) {
            this.isEndReached = true;
          }
          const changes = querySnapshot.docChanges();
          changes.forEach((change) => this.handleDocumentChange(change));
        });
    },

    async loadFirstPage() {
      await onSnapshot(
            queryBusinessPortalNotificationsCollection(
                where("businessProfileId", "==", this.userProfile.businessProfileId),
                orderBy("createdOn", "desc"),
                startAt(this.firstDocumentOnLoad),
                limit(this.pageSize),
            ),
            (querySnapshot) => {
          const changes = querySnapshot.docChanges();
          changes.forEach(async (change) => this.handleDocumentChange(change));
        });
    },

    async loadNewDocuments() {
      await onSnapshot(
            queryBusinessPortalNotificationsCollection(
                where("businessProfileId", "==", this.userProfile.businessProfileId),
                orderBy("createdOn", "desc"),
                startAt(this.firstDocumentOnLoad),
                endBefore(this.firstDocumentOnLoad),
            ),
            (querySnapshot) => {
          const changes = querySnapshot.docChanges();
          changes.forEach((change) => this.handleDocumentChange(change));
        });
    },

    setDisplayImageUrl(document) {
      //Get the diaplyImage url for the use
      let storageRef = getStorageRef(`userDisplayImages/${document.uid}.png`);
      let displayImageUrl = "";
      getDownloadUrlForRef(storageRef)
        .then((url) => {
          document.displayImageUrl = url.toString();
        })
        .catch(function (error) {
          //No display image found
        });
    },
    async handleDocumentChange(change) {
      let addedDocument;
      if (change.type === "added" || change.type == "modified") {
        let data = change.doc.data({ serverTimestamps: "estimate" });
        addedDocument = {
          ...data,
          displayImageUrl: "",
          subject: NotificationUtility.formatNotificationSubject(data),
          type: data.type,
          createdOn: data.createdOn.toDate(),
          businessCardId: data.businessCardId,
          uid: data.uid,
          userName: data.userName,
          notificationId: change.doc.id,
          isRead: data.isRead,
        };
      }
      if (change.type === "added" && addedDocument.subject != "") {
        if (
          this.notifications.length != 0 &&
          addedDocument.createdOn > this.notifications[0].createdOn
        ) {
          this.notifications.unshift(addedDocument);
        } else {
          this.notifications.push(addedDocument);
          this.lastLoadedDocumnet = change.doc;
        }
        this.setDisplayImageUrl(addedDocument);
      } else if (change.type == "modified" && addedDocument.subject != "") {
        //Replace the old object within the array with the modified object
        this.notifications.forEach((item, index) => {
          if (item.notificationId == change.doc.id) {
            //Start: retian the old objects display Image Url
            addedDocument.displayImageUrl = item.displayImageUrl;
            //End: retian the old objects display Image Url
            this.notifications.splice(index, 1, addedDocument);
          }
        });
      } else if (change.type == "removed") {
        //Remove the object from the array
        this.notifications.forEach((item, index) => {
          if (item.notificationId == change.doc.id) {
            this.notifications.splice(index, 1);
          }
        });
      }
    },

    async initializeFirstDocumentOnLoad() {
      await getQueryBusinessPortalNotificationsCollection(
        where("businessProfileId", "==", this.userProfile.businessProfileId),
        orderBy("createdOn", "desc"),
        limit(1)
        )
        .then((querySnapshot) => {
          querySnapshot.forEach((doc) => {
            this.firstDocumentOnLoad = doc;
          });
        })
        .catch((error) => {
          console.log("Error while retriving the first document on load");
          console.log(error);
        });
    },

    async deleteNotifications() {
      const db = getFirestore();
      const batch = writeBatch(db);
      this.selected.forEach((notification) => {
        const notificationDocumentRef = businessPortalNotificationsDocRef(notification.notificationId);
        batch.delete(notificationDocumentRef);
      });
      // Commit the batch
      batch.commit().then(() => {
        if (this.selected.length == 1)
          this.snackbarText = "Notification deleted successfully!";
        else
          this.snackbarText = `${this.selected.length} notifications deleted successfully!`;
        this.snackbarTimeout = 5000;
        this.snackbar = true;
        this.selected = [];
      });
    },

    async markNotificationAsRead() {
      const db = getFirestore();
      const batch = writeBatch(db);
      var noOfNotificationWritten = 0;
      this.selected.forEach((notification) => {
        if (!notification.isRead) {
          let notificationDocumentRef = businessPortalNotificationsDocRef(notification.notificationId);
          batch.update(notificationDocumentRef, { isRead: true });
          batch.update(this.businessPortalStatsDocumentRef, {
            noOfNewNotifications: increment(-1),
          });
          noOfNotificationWritten++;
        }
      });
      // Commit the batch
      batch.commit().then(() => {
        if (noOfNotificationWritten == 0)
          this.snackbarText =
            "All of the selected notifications are already marked as read";
        else if (noOfNotificationWritten == 1)
          this.snackbarText = `${noOfNotificationWritten} notification marked as read.`;
        else
          this.snackbarText = `${noOfNotificationWritten} notifications marked as read.`;
        this.snackbarTimeout = 5000;
        this.snackbar = true;
        this.selected = [];
      });
    },

    async markNotificationAsUnread() {
      const db = getFirestore();
      const batch = writeBatch(db);
      var noOfNotificationWritten = 0;
      this.selected.forEach((notification) => {
        if (notification.isRead) {
          const notificationDocumentRef = businessPortalNotificationsDocRef(notification.notificationId);
          batch.update(notificationDocumentRef, { isRead: false });
          batch.update(this.businessPortalStatsDocumentRef, {
            noOfNewNotifications: increment(1),
          });
          noOfNotificationWritten++;
        }
      });
      // Commit the batch
      batch.commit().then(() => {
        if (noOfNotificationWritten == 0)
          this.snackbarText =
            "All of the selected notifications are already marked as unread";
        else if (noOfNotificationWritten == 1)
          this.snackbarText = `${noOfNotificationWritten} notification marked as unread.`;
        else
          this.snackbarText = `${noOfNotificationWritten} notifications marked as unread.`;
        this.snackbarTimeout = 5000;
        this.snackbar = true;
        this.selected = [];
      });
    },

    getInitials(name) {
      return Utility.getInitials(name);
    },
  },
  data() {
    return {
      presentHandleNotificationComponent: false,
      selectedNotification: {},
      isEndReached: false,
      lastLoadedDocumnet: null,
      pageSize: 20,
      firstDocumentOnLoad: null,
      db: getFirestore(),
      snackbar: false,
      snackbarText: "",
      snackbarTimeout: 5000,
      selected: [],
      headers: [
        {
          text: "Subject",
          align: "start",
          value: "subject",
        },
        { text: "Date", value: "createdOn" },
      ],
      notifications: [],
    };
  },
  computed: {
    ...mapGetters(["userProfile", "businessPortalStats", "multiConnectEvents"]),
    businessPortalStatsDocumentRef() {
      return businessPortalStatsDocRef(this.userProfile.businessProfileId);
    },
  },
};
</script>
<style scoped>
.row-pointer >>> tbody tr :hover {
  cursor: pointer;
}
</style>
